import { classNames } from "primereact/utils";
import { useAppContext } from "../../../../AppContext";
import { getCurrentUserRole } from "../../../infrastructure/helpers/userHelper";
import { UserRole } from "../../../enums";
import { useDraggableFab } from "./useDraggableFab";
import styles from "./DraggableBackButton.module.scss";

interface IDraggableBackButtonProps {
  /** What "back" does. A plain tap calls this; a drag only repositions. */
  onBack: () => void;
  ariaLabel?: string;
  title?: string;
}

/**
 * Shared floating, draggable circular "back" control.
 *
 * It owns only the look and the drag-to-reposition behaviour (position is
 * remembered across sessions — see {@link useDraggableFab}). Callers decide
 * what "back" means: history navigation on normal detail pages, or stepping
 * out of a self-managed flow such as the booking wizard.
 *
 * A press that barely moves counts as a tap and triggers {@link onBack}; only
 * an actual drag repositions the button, so the two never get confused.
 *
 * It is an admin/staff convenience, so it is hidden from signed-out visitors and
 * from clients (role `User`) — only authenticated staff roles ever see it. This
 * gate lives here so every instance (the global one and the wizards') is covered.
 */
export const DraggableBackButton = ({
  onBack,
  ariaLabel = "Назад",
  title,
}: IDraggableBackButtonProps) => {
  const { isAuthenticated, currentUser } = useAppContext();
  const { position, isDragging, dragHandlers, consumeClickAfterDrag } =
    useDraggableFab();

  const role = getCurrentUserRole(currentUser);
  const isStaffUser = role !== undefined && role !== UserRole.User;
  if (!isAuthenticated || !isStaffUser) {
    return null;
  }

  const handleClick = () => {
    // Ignore the click the browser fires at the end of a drag gesture.
    if (consumeClickAfterDrag()) {
      return;
    }
    onBack();
  };

  return (
    <button
      type="button"
      className={classNames(styles.backButton, {
        [styles.dragging]: isDragging,
      })}
      style={{ top: position.y, left: position.x }}
      onClick={handleClick}
      aria-label={ariaLabel}
      title={title ?? "Назад (задръжте и плъзнете, за да преместите)"}
      {...dragHandlers}
    >
      {/* Inlined chevron so it stays optically centred inside the round button.
          The shared ArrowLeft icon uses a 100x125 viewBox (content only fills
          the top 100), which pushes the glyph upward. Here a square viewBox
          centres it vertically, and the 5-unit x offset compensates for the
          glyph's mass sitting right of its bounding-box centre (area centroid
          ~x=55), so it reads centred left-to-right too. */}
      <svg
        className={styles.icon}
        viewBox="5 0 100 100"
        width="13"
        height="13"
        fill="#fff"
        aria-hidden="true"
        focusable="false"
      >
        <polygon points="75.3,99.3 74.7,100 24.7,50 74.7,0 75.3,0.7 75.3,22.6 48,50 75.3,77.4 " />
      </svg>
    </button>
  );
};
